<template>
	<div v-if="loading" class="limit-height" aria-busy="true" v-test="loadingTestId">
		<template v-for="n in safeSkeletonRowCount" :key="n">
			<slot name="skeleton" :index="n - 1" />
		</template>
	</div>
	<div v-else-if="items.length === 0" class="limit-height d-flex align-center justify-center pa-6 text-medium-emphasis text-center">
		{{ emptyText }}
	</div>
	<v-virtual-scroll v-else ref="scrollRef" :items="items" :height="height" :item-height="itemHeight" class="limit-height">
		<template #default="{ item }">
			<!-- A grouped list interleaves non-selectable group headings with its options. A heading
				 gets no checkbox, no click target and no #item slot, so it can never be toggled or
				 counted as a selection. Callers that pass no isHeader predicate render exactly as
				 before. -->
			<v-list-subheader v-if="isHeader?.(item)" :key="item.id" class="filter-menu-subheader font-weight-bold bg-grey-lighten-2">
				<slot name="header" :item="item" />
			</v-list-subheader>
			<v-list-item
				v-else
				:key="item.id"
				class="w-100"
				:class="[itemClass, { 'selected-row': isSelected(item) }]"
				v-bind="rowAttrs ? rowAttrs(item) : undefined"
				@click.stop="emit('toggle', item)">
				<template #prepend>
					<v-list-item-action start>
						<v-checkbox-btn :model-value="isSelected(item)"></v-checkbox-btn>
					</v-list-item-action>
				</template>
				<slot name="item" :item="item" :selected="isSelected(item)" />
			</v-list-item>
		</template>
	</v-virtual-scroll>
</template>

<script setup lang="ts" generic="T extends { id: string }">
	import { computed, ref } from "vue";

	interface Props {
		items: T[];
		loading?: boolean;
		isSelected: (item: T) => boolean;
		loadingTestId: string;
		skeletonRowCount?: number;
		height?: number | string;
		itemHeight?: number | string;
		itemClass?: string;
		rowAttrs?: (item: T) => Record<string, string>;
		isHeader?: (item: T) => boolean;
		emptyText?: string;
	}

	// Number of placeholder rows shown while a filter loads — roughly fills the 250px menu so the
	// dropdown doesn't visibly resize when the real (virtualized) rows replace the skeleton.
	const SKELETON_ROW_COUNT = 6;

	const props = withDefaults(defineProps<Props>(), {
		loading: false,
		skeletonRowCount: SKELETON_ROW_COUNT,
		height: 250,
		itemHeight: 50,
		itemClass: undefined,
		rowAttrs: undefined,
		isHeader: undefined,
		emptyText: "No matching results"
	});

	// Clamp to a non-negative integer — v-for="n in N" throws a RangeError for a negative N
	// (break-it round 2), and Math.floor() on a non-numeric value (e.g. a Symbol) throws too
	// (break-it round 3). All current callers omit the prop and inherit the default, but this
	// keeps the shared component's contract safe for any future caller.
	const safeSkeletonRowCount = computed((): number =>
		typeof props.skeletonRowCount === "number" && Number.isFinite(props.skeletonRowCount)
			? Math.max(0, Math.floor(props.skeletonRowCount))
			: SKELETON_ROW_COUNT
	);

	const emit = defineEmits<{ (e: "toggle", item: T): void }>();

	defineSlots<{
		item: (props: { item: T; selected: boolean }) => any;
		// Only a grouped caller (one that also passes isHeader) fills this.
		header?: (props: { item: T }) => any;
		skeleton: (props: { index: number }) => any;
	}>();

	const scrollRef = ref<any>(null);

	function resetScroll(): void {
		const el = scrollRef.value?.$el;
		if (el) el.scrollTop = 0;
	}

	defineExpose({ resetScroll });
</script>

<style scoped>
	.limit-height {
		max-height: 250px;
		overflow-y: auto;
	}

	/* Vuetify gives a subheader its inline padding through `.v-list--density-* .v-list-subheader`,
	   so it only ever indents inside a v-list. This list body is a v-virtual-scroll, so the heading
	   would sit flush against the menu edge while every checkbox row below it is indented. 16px is
	   density-default's own value (`calc(16px + var(--indent-padding))` with no indent), measured
	   against the pre-LSP-19305 autocomplete menu, whose VList takes the default density. Height
	   needs no such rule: `.v-list-subheader`'s base min-height is already 40px in both contexts. */
	.filter-menu-subheader {
		padding-inline-start: 16px;
	}
</style>
